import { z } from 'zod';
import { CreateMediaUploadResponseSchema } from '../media/files.schemas';
import { DOCUMENT_MAX_SIZE_BYTES } from './documents.schemas';
import { refersTo } from '../common/refers-to';

/**
 * The two fixed exposé versions of an Objekt (IMM-1056), in display order:
 * the Vollversion (with the address and all details), then the Kurzexposé
 * (without the address and sensitive details). German labels live in i18n.
 */
export const EXPOSE_VERSIONS = ['FULL', 'SHORT'] as const;

export const ExposeVersionSchema = z.enum(EXPOSE_VERSIONS);

export type ExposeVersion = z.infer<typeof ExposeVersionSchema>;

export const PropertyExposesPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});

export const PropertyExposeVersionPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  version: ExposeVersionSchema,
});

export const PropertyExposeFileSchema = z.object({
  fileName: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedAt: z.iso.datetime(),
  // Null when immoOS (an operator actor) filed it — `uploadedByName` answers "immoOS" then.
  uploadedByMemberId: refersTo('member', z.uuid()).nullable(),
  // Null when the uploader is no longer an active member; the frontend renders its placeholder.
  uploadedByName: z.string().nullable(),
});

export type PropertyExposeFile = z.infer<typeof PropertyExposeFileSchema>;

/** One version slot; `file` is null while the version is empty. */
export const PropertyExposeVersionSchema = z.object({
  version: ExposeVersionSchema,
  file: PropertyExposeFileSchema.nullable(),
});

export type PropertyExposeVersion = z.infer<typeof PropertyExposeVersionSchema>;

export const ListPropertyExposesResponseSchema = z.object({
  // Always exactly two entries, `FULL` then `SHORT`.
  versions: z.array(PropertyExposeVersionSchema),
});

export type ListPropertyExposesResponse = z.infer<typeof ListPropertyExposesResponseSchema>;

export const ReadPropertyExposeResponseSchema = z.object({
  url: z.url(),
  expiresInSeconds: z.number().int().positive(),
});

export type ReadPropertyExposeResponse = z.infer<typeof ReadPropertyExposeResponseSchema>;

/**
 * Server-authoritative exposé upload policy (IMM-1056): PDF only, at most the
 * platform's document limit. The property module hands it to the media port;
 * the client never supplies it.
 */
export const EXPOSE_ALLOWED_CONTENT_TYPES = ['application/pdf'] as const;

export const EXPOSE_MAX_SIZE_BYTES = DOCUMENT_MAX_SIZE_BYTES;

/** The media label an exposé upload carries; the one thing that tells its bytes from a document's. */
export const EXPOSE_MEDIA_DESCRIPTION = 'property-expose';

export const CreatePropertyExposeUploadBodySchema = z
  .object({
    // A plain string, not a PDF literal: a wrong type must answer the pinned
    // `not-a-pdf` refusal, not a generic validation error.
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
    originalFilename: z.string().trim().min(1).max(255),
  })
  .strict();

export type CreatePropertyExposeUploadBody = z.infer<typeof CreatePropertyExposeUploadBodySchema>;

export const CreatePropertyExposeUploadResponseSchema = CreateMediaUploadResponseSchema;

export const FilePropertyExposeBodySchema = z.object({ fileId: z.uuid() }).strict();

export type FilePropertyExposeBody = z.infer<typeof FilePropertyExposeBodySchema>;

export const FilePropertyExposeResponseSchema = PropertyExposeVersionSchema;

export const RemovePropertyExposeResponseSchema = PropertyExposeVersionSchema;
